import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Save, Undo2 } from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import orchardService from '../../services/orchard';
import PlantingPlanFields, { PlanCultivarPicker, PollinatorList } from './PlantingPlanFields';
import { cultivarDisplayName, draftFromPlan, draftToSave, emptyPlanDraft, type PlanDraft } from './plantingPlanDraft';
import type { CultivarArea, PlantingPlan, RowOverride } from '../../types/orchard';

interface PlantingPlanEditorProps {
  area: CultivarArea;
  drawingFirstRow: boolean;
  onRequestDrawFirstRow: () => void;
  onCancelDrawFirstRow: () => void;
  /** Set by the parent when the user finishes drawing the first-row line. */
  pendingFirstRowLine?: { start: [number, number]; end: [number, number] } | null;
  /** Parent clears its pending line after we consume it. */
  onFirstRowLineConsumed?: () => void;
  /** The layout on screen — saved, or previewed while editing — for the map. */
  onPlanChange?: (plan: PlantingPlan | null) => void;
  /** After a save: the area's tree count and spacing now come from the rows. */
  onSaved?: (plan: PlantingPlan) => void;
}

function PlantingPlanEditor({
  area,
  drawingFirstRow,
  onRequestDrawFirstRow,
  onCancelDrawFirstRow,
  pendingFirstRowLine,
  onFirstRowLineConsumed,
  onPlanChange,
  onSaved,
}: PlantingPlanEditorProps) {
  const [saved, setSaved] = useState<PlantingPlan | null>(null);
  const [preview, setPreview] = useState<PlantingPlan | null>(null);
  const [draft, setDraft] = useState<PlanDraft>(emptyPlanDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Parents pass inline callbacks. Depending on them re-created loadPlan on
  // every parent render, and loadPlan re-rendered the parent: the page sat on
  // a spinner forever. Refs break the cycle.
  const onPlanChangeRef = useRef(onPlanChange);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onPlanChangeRef.current = onPlanChange;
    onSavedRef.current = onSaved;
  });

  const loadPlan = useCallback(async () => {
    setError(null);
    try {
      const res = await orchardService.getPlantingPlan(area.id);
      if (res.success && res.data) {
        setSaved(res.data);
        setDraft(draftFromPlan(res.data));
        setDirty(false);
        onPlanChangeRef.current?.(res.data);
      } else {
        setError('Failed to load the planting plan.');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load the planting plan.');
    } finally {
      setLoading(false);
    }
  }, [area.id]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // Consume a first-row line drawn on the map.
  useEffect(() => {
    if (!pendingFirstRowLine) return;
    setDraft((d) => ({
      ...d,
      first_row_start_point: pendingFirstRowLine.start,
      first_row_end_point: pendingFirstRowLine.end,
    }));
    setDirty(true);
    onFirstRowLineConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume once per drawn line
  }, [pendingFirstRowLine]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await orchardService.savePlantingPlan(area.id, draftToSave(draft));
      if (res.success && res.data) {
        setSaved(res.data);
        setDraft(draftFromPlan(res.data));
        setDirty(false);
        onPlanChangeRef.current?.(res.data);
        onSavedRef.current?.(res.data);
      } else {
        setError('Failed to save the planting plan.');
      }
    } catch (e: any) {
      // A 403 here is the planting licence: the rows hold more trees than it allows.
      setError(e?.message || 'Failed to save the planting plan.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!saved) return;
    setDraft(draftFromPlan(saved));
    setDirty(false);
  };

  // ---------------------------------------------------------------------------
  // Per-row overrides — against saved rows only; a row number means nothing
  // until the layout it numbers has been saved.
  // ---------------------------------------------------------------------------

  const names = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of [...(saved?.cultivars ?? []), ...(preview?.cultivars ?? [])]) m.set(c.id, cultivarDisplayName(c));
    return (id: number) => m.get(id) || '';
  }, [saved, preview]);

  const overrideMap = useMemo(() => {
    const m = new Map<number, RowOverride>();
    saved?.overrides.forEach((o) => m.set(o.row_number, o));
    return m;
  }, [saved]);

  const [editingOverride, setEditingOverride] = useState<RowOverride | null>(null);

  const openRowEdit = (rowNumber: number) => {
    const existing = overrideMap.get(rowNumber);
    setEditingOverride(
      existing
        ? { ...existing, pollinators: existing.pollinators ? [...existing.pollinators] : null }
        : { row_number: rowNumber, primary_cultivar_id: null, pollinators: null, notes: null }
    );
  };

  const saveRowOverride = async () => {
    if (!editingOverride) return;
    const res = await orchardService.upsertRowOverride(area.id, editingOverride.row_number, {
      primary_cultivar_id: editingOverride.primary_cultivar_id,
      pollinators: editingOverride.pollinators?.filter((p) => p.cultivar_id > 0) ?? null,
      notes: editingOverride.notes,
    });
    if (res.success) {
      setEditingOverride(null);
      await loadPlan();
    }
  };

  const deleteRowOverride = async (rowNumber: number) => {
    const res = await orchardService.deleteRowOverride(area.id, rowNumber);
    if (res.success) await loadPlan();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const savedRows = saved?.rows ?? [];

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">{error}</div>
      )}

      <PlantingPlanFields
        draft={draft}
        onChange={(next) => {
          setDraft(next);
          setDirty(true);
        }}
        boundary={area.boundary_geometry}
        cultivarId={area.cultivar_id}
        cultivarName={
          area.cultivar_name
            ? area.cultivar_trade_name
              ? `${area.cultivar_name} (${area.cultivar_trade_name})`
              : area.cultivar_name
            : null
        }
        areaId={area.id}
        drawingFirstRow={drawingFirstRow}
        onRequestDrawFirstRow={onRequestDrawFirstRow}
        onCancelDrawFirstRow={onCancelDrawFirstRow}
        onPreview={(p) => {
          setPreview(p);
          onPlanChangeRef.current?.(p);
        }}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="primary" onClick={handleSave} disabled={!dirty || saving} isLoading={saving}>
          <Save className="h-4 w-4 mr-1" /> Save rows
        </Button>
        {dirty && !saving && (
          <Button type="button" variant="outline" onClick={handleReset}>
            <Undo2 className="h-4 w-4 mr-1" /> Reset changes
          </Button>
        )}
        {dirty && (
          <span className="text-xs text-neutral-500">
            Saving sets this area's tree count and spacing from the rows.
          </span>
        )}
      </div>

      {savedRows.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-neutral-900 mb-1">Individual rows</h3>
            <p className="text-xs text-neutral-500 mb-3">
              Each row follows the pattern. Tap a row to plant it differently.
              {dirty && ' Save your changes first — row numbers are the saved layout.'}
            </p>
            <div className="max-h-[300px] overflow-auto border border-neutral-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 sticky top-0">
                  <tr className="text-left text-xs text-neutral-600">
                    <th className="px-2 py-2">#</th>
                    <th className="px-2 py-2">Cultivar</th>
                    <th className="px-2 py-2 text-right">Trees</th>
                    <th className="px-2 py-2 text-right">Length</th>
                    <th className="px-2 py-2 text-center">Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {savedRows.map((row) => {
                    const override = overrideMap.get(row.row_number);
                    return (
                      <tr
                        key={row.row_number}
                        className="border-t hover:bg-primary-50 cursor-pointer"
                        onClick={() => openRowEdit(row.row_number)}
                      >
                        <td className="px-2 py-1.5 font-mono">{row.row_number}</td>
                        <td className="px-2 py-1.5">
                          {row.primary_cultivar_id
                            ? names(row.primary_cultivar_id) || `#${row.primary_cultivar_id}`
                            : '—'}
                          {row.sources.primary === 'override' && (
                            <span className="ml-1 inline-block text-[10px] font-semibold text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded">
                              CHANGED
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">{row.tree_count}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">{row.length_meters.toFixed(1)} m</td>
                        <td className="px-2 py-1.5 text-center">
                          {override ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteRowOverride(row.row_number);
                              }}
                              className="text-xs text-error-600 hover:underline"
                              title="Put this row back on the pattern"
                            >
                              Undo
                            </button>
                          ) : (
                            <span className="text-xs text-neutral-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {editingOverride && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setEditingOverride(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-base font-semibold">Row {editingOverride.row_number}</h3>
              <button onClick={() => setEditingOverride(null)} className="text-neutral-500 hover:text-neutral-900">
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Cultivar in this row</label>
                <PlanCultivarPicker
                  value={editingOverride.primary_cultivar_id}
                  valueName={
                    editingOverride.primary_cultivar_id
                      ? names(editingOverride.primary_cultivar_id) || editingOverride.primary_cultivar_name || null
                      : null
                  }
                  onChange={(opt) =>
                    setEditingOverride({ ...editingOverride, primary_cultivar_id: opt ? opt.id : null })
                  }
                  placeholder="Leave blank to follow the pattern"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Pollinators in this row</label>
                <PollinatorList
                  pollinators={editingOverride.pollinators || []}
                  cultivarNameLookup={names}
                  onChange={(next) => setEditingOverride({ ...editingOverride, pollinators: next.length ? next : null })}
                />
                <p className="text-[11px] text-neutral-500 mt-1">Leave empty to use the block's pollinators.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Notes</label>
                <textarea
                  className="input-professional w-full text-sm"
                  rows={2}
                  value={editingOverride.notes || ''}
                  onChange={(e) => setEditingOverride({ ...editingOverride, notes: e.target.value || null })}
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingOverride(null)}>
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={saveRowOverride}>
                Save row
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlantingPlanEditor;
